import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useModalDrag } from '../../hooks/useModalDrag';
import { v1WeightCascade, type WeightUnit } from '../../utils/v1WeightConversion';
import ModalHeader from '../ui/ModalHeader';
import DateTimePicker from '../DateTimePicker';
import { parseDate, formatDateStr } from '../tablemaint/DateField';
import type { DupeOrderRow } from '../../services/orders.service';

/* Dupe Order — step 2, V1's "Enter equipment and reference numbers" form 2
 * (`lib_editrecord.pl:21785-22062`, transcribed in
 * docs/plans/2026-08-13-dupe-order-v1-rules.md §3.2).
 *
 * PURE UI: it collects N rows of the 18 @dupescreen fields and hands them back
 * through `onSubmit`. No API calls, no permission checks, no OOG/hidden-field
 * seeding — `OrderDupeActions` owns the POST and the server owns the hidden
 * copysets (plan §"Deviate": no `check_oogdupe` JS port).
 *
 * Deliberate deviations from V1's form:
 *  - WYSIWYG cells (2026-08-28 design): every cell PRE-FILLS with the source
 *    value that will copy (see `prefillRow` for the four exceptions), and the
 *    payload rides with `explicitValues: true`, so blank means BLANK — V1's
 *    invisible blank-inherits contract is gone from this grid.
 *  - removable columns: the header × pulls a field out of the copy entirely
 *    (its cells blank on every row) and the "Add column…" menu brings it back
 *    re-prefilled. Per-batch only — nothing is persisted.
 *  - a per-row remove button replaces V1's `dupe_<i>_deleted` hidden flag, and
 *    an "Add row" button lets the count grow after the flags modal (V1 made
 *    you go back); both stay inside the server's 1..199 clamp.
 *  - the duplicate-container block is an inline error, not `alert()`.
 *  - the four date cells are the app's DateTimePicker rather than plain text
 *    boxes, so they carry its numeric entry shortcuts (`0` today, `5` +5 days,
 *    `-1` yesterday, `n` now) and a calendar. The wire format is untouched.
 *  - arrow keys walk the grid in all four directions (V1 had only its Enter
 *    move). See `handleKeyDown` for the two conflicts this has to respect: the
 *    text caret and the ON/BY <select>.
 */

type DupeField = keyof DupeOrderRow;

type Row = Record<DupeField, string>;

type ColumnKind = 'text' | 'date' | 'datetime' | 'select';

interface ColumnSpec {
  key: DupeField;
  /** Short header label. `bol`'s is overridden per movetype — see `bolLabel`. */
  label: string;
  kind: ColumnKind;
  /** Tailwind min-width for the column; the grid scrolls horizontally. */
  width: string;
}

/** @dupescreen order, verbatim (v1-rules §3.1) — the wire order the API expects. */
const COLUMNS: ColumnSpec[] = [
  { key: 'container', label: 'Equipment#', kind: 'text', width: 'min-w-[120px]' },
  { key: 'bol', label: 'BOL', kind: 'text', width: 'min-w-[120px]' },
  { key: 'sealnumber', label: 'Seal No', kind: 'text', width: 'min-w-[110px]' },
  { key: 'chassisid', label: 'Chass#', kind: 'text', width: 'min-w-[110px]' },
  { key: 'commodity', label: 'Commodity', kind: 'text', width: 'min-w-[130px]' },
  { key: 'piececount', label: 'Pieces', kind: 'text', width: 'min-w-[80px]' },
  { key: 'lbs', label: 'Lbs', kind: 'text', width: 'min-w-[90px]' },
  { key: 'kgs', label: 'Kgs', kind: 'text', width: 'min-w-[90px]' },
  { key: 'metrictons', label: 'MT', kind: 'text', width: 'min-w-[80px]' },
  { key: 'po', label: 'PO', kind: 'text', width: 'min-w-[110px]' },
  { key: 'billtorefno', label: 'Bill To Ref#', kind: 'text', width: 'min-w-[120px]' },
  { key: 'etadate', label: 'ETA', kind: 'date', width: 'min-w-[140px]' },
  { key: 'targetonorby', label: 'ON/BY', kind: 'select', width: 'min-w-[80px]' },
  { key: 'targetdeliverydate', label: 'Target Deliv', kind: 'date', width: 'min-w-[140px]' },
  { key: 'lastfreedaydate', label: 'LFD', kind: 'date', width: 'min-w-[140px]' },
  { key: 'cutoffdatetime', label: 'Cutoff', kind: 'datetime', width: 'min-w-[180px]' },
  { key: 'origapptnumber', label: 'Orig Appt#', kind: 'text', width: 'min-w-[110px]' },
  { key: 'destapptnumber', label: 'Dest Appt#', kind: 'text', width: 'min-w-[110px]' },
];

/** V1 writes the two appointment numbers verbatim — everything else is `uc()`d. */
const VERBATIM_FIELDS = new Set<DupeField>(['origapptnumber', 'destapptnumber']);

const WEIGHT_FIELDS = new Set<DupeField>(['lbs', 'kgs', 'metrictons']);

/** Server clamp (plan §B2) — the grid never lets you build a payload it would reject. */
export const MAX_DUPE_ROWS = 199;

/**
 * V1 `bol` column header by movetype (`lib_editrecord.pl:21826-21836`).
 * V1 left the default label alone for movetypes outside its three branches;
 * we fold that tail into "Ref#" — the same label its DOMESTIC/REPO/INBOUND/
 * OUTBOUND branch uses, and the only sensible generic for a reference number.
 */
export const bolLabel = (movetype: string): string => {
  const mt = (movetype || '').trim().toUpperCase();
  if (mt === 'IMPORT') return 'BOL';
  if (/EXPORT|RAIL TO PORT/.test(mt)) return 'BKG';
  return 'Ref#';
};

/**
 * `cutoffdatetime` keeps riding the wire as 'YYYY-MM-DDTHH:mm' — exactly what
 * the `<input type="datetime-local">` it replaces produced, and a shape the
 * backend's `_convert_datetime` passes straight through (it only swaps the `T`
 * for a space). So the picker swap is display-only: the payload the server sees
 * is unchanged.
 *
 * The three date-only columns reuse DateField's `parseDate`/`formatDateStr`
 * ('YYYY-MM-DD'), the same pair AccessorialEditModal round-trips; nothing
 * shared covers the datetime pair, so it lives here.
 */
const pad2 = (n: number): string => String(n).padStart(2, '0');

export const parseDateTimeLocal = (v: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(v || '');
  // Blank, a zero-date, or a date-only value all fall through to parseDate.
  if (!m || m[1] === '0000') return parseDate(v);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
};

export const formatDateTimeLocal = (d: Date | null): string =>
  d ? `${formatDateStr(d)}T${pad2(d.getHours())}:${pad2(d.getMinutes())}` : '';

const blankRow = (): Row =>
  COLUMNS.reduce((acc, c) => { acc[c.key] = ''; return acc; }, {} as Row);

/** The four fields a dupe never copies (server: `_NO_INHERIT_SCREEN_FIELDS` +
 * the two appt-number leg writes are skipped when blank) — their cells start
 * blank so the grid keeps showing exactly what will land. */
const NO_PREFILL_FIELDS = new Set<DupeField>([
  'container', 'lastfreedaydate', 'origapptnumber', 'destapptnumber',
]);

/**
 * One row pre-filled with the source values that will copy — WYSIWYG: the
 * payload is explicit (`explicitValues: true`), so whatever a cell holds is
 * exactly what lands on the new pro, and clearing a cell really clears it.
 *
 * Date/datetime values round-trip through the same parse/format pair the
 * picker cells use, so the row state never holds a value the picker cannot
 * display (a zero-date or junk source normalizes to '', which is also what it
 * would have landed as). ON/BY is sanitized to its <select> domain for the
 * same reason — an out-of-domain source value must not hide behind a
 * blank-looking select and still ride the wire.
 */
export const prefillRow = (source: DupeOrderRow): Row => {
  const row = blankRow();
  for (const c of COLUMNS) {
    if (NO_PREFILL_FIELDS.has(c.key)) continue;
    const raw = (source[c.key] ?? '').trim();
    if (c.kind === 'date') row[c.key] = formatDateStr(parseDate(raw));
    else if (c.kind === 'datetime') row[c.key] = formatDateTimeLocal(parseDateTimeLocal(raw));
    else if (c.key === 'targetonorby') {
      const u = raw.toUpperCase();
      row[c.key] = u === 'ON' || u === 'BY' ? u : '';
    } else row[c.key] = raw;
  }
  return row;
};

const makeRows = (n: number, source: DupeOrderRow): Row[] =>
  Array.from(
    { length: Math.min(Math.max(Math.trunc(n) || 1, 1), MAX_DUPE_ROWS) },
    () => prefillRow(source),
  );

/** Parse a user-typed weight; null when blank or non-numeric (leave the peers alone). */
const toNumber = (v: string): number | null => {
  const t = (v || '').trim().replace(/,/g, '');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/**
 * V1 `kgconvert`/`mtconvert` arithmetic, wired on the dupe screen at
 * lib_editrecord.pl:21068-21078 (`kgconvert("1"/"2", …, "dupe_N_…")` and
 * `mtconvert("3", "dupe_N_")`) — the SAME JS the order form uses, so this
 * delegates to the shared module rather than keeping its own copy.
 *
 * This block used to say the V1 JS bodies were not in the checkout and that its
 * formulas came from the dupe-order spec instead. They ARE available
 * (/perl/js/common_html5.js:1630-1810), and the spec-derived guesses were wrong
 * on every arm (#33560): lbs->kgs is *0.4536 not /2.2046, mt->kgs is *999.9988
 * not *1000, mt->lbs is *2204.62 rather than chained through kgs, and every
 * result carries one decimal instead of being rounded to a whole number.
 *
 * `isNexa` is accepted but NO CALLER SUPPLIES IT YET. V1's Nexa branch reads
 * `order_customercode`, which is in the DOM when its dupe modal opens, so V1
 * does apply the customer-4525 factors here — this grid has no customercode in
 * its props to pass down. A pre-existing gap, not a regression (the previous
 * code had no Nexa handling either), left open because closing it means
 * threading customercode through DupeOrderGridProps and every caller for one
 * customer. The parameter is here so that wiring is a one-line change.
 *
 * Returns ONLY the peer fields whose value actually changes, so a blur that
 * converts nothing leaves the row (and its React identity) untouched.
 */
export const convertWeights = (
  row: Row,
  edited: DupeField,
  opts: { isNexa?: boolean } = {},
): Partial<Row> => {
  const patch: Partial<Row> = {};
  const set = (key: DupeField, value: string) => {
    if (row[key] !== value) patch[key] = value;
  };
  const source: WeightUnit | null =
    edited === 'lbs' ? 'lbs' : edited === 'kgs' ? 'kgs'
      : edited === 'metrictons' ? 'metrictons' : null;
  if (source === null) return patch;
  const n = toNumber(row[edited]);
  if (n === null) return patch;
  const w = v1WeightCascade(source, n, { isNexa: opts.isNexa });
  // Only the PEER fields are written back; V1 leaves the field the user typed
  // in exactly as typed (kgconvert never reassigns `obj.value` on this path).
  if (source !== 'lbs') set('lbs', w.lbs);
  if (source !== 'kgs') set('kgs', w.kgs);
  if (source !== 'metrictons') set('metrictons', w.metrictons);
  return patch;
};

/**
 * V1 `checkDupeContainers()` (`lib_editrecord.pl:22037-22054`): the first row
 * that repeats a non-blank container blocks the submit, naming both 1-based
 * line numbers. Blank containers never collide (they inherit nothing — the
 * source's container is deliberately NOT copied).
 */
export const findDuplicateContainer = (rows: Row[]): { first: number; second: number } | null => {
  const seen = new Map<string, number>();
  for (let i = 0; i < rows.length; i++) {
    const c = (rows[i].container || '').trim().toUpperCase();
    if (c === '') continue;
    const prior = seen.get(c);
    if (prior !== undefined) return { first: prior, second: i + 1 };
    seen.set(c, i + 1);
  }
  return null;
};

export interface DupeOrderGridProps {
  open: boolean;
  /** Submit in flight — locks every input, the row buttons and Submit. */
  busy?: boolean;
  /** The source order's per-column display values (V1's `dupevalue_<field>` header row). */
  sourceValues: DupeOrderRow;
  /** Source masterpro, or the literal 'NEW MASTER' sentinel when samemaster='N'. */
  masterProLabel: string;
  movetype: string;
  copyFlags: { copymrbl: string; copynotes: string; copyaccessorials: string; copydocuments: string };
  /** Initial row count, carried over from the flags modal. */
  dupecount: number;
  onSubmit: (rows: DupeOrderRow[]) => void;
  /** Back to the flags modal. */
  onBack: () => void;
  onClose: () => void;
}

const DupeOrderGrid: React.FC<DupeOrderGridProps> = ({
  open,
  busy = false,
  sourceValues,
  masterProLabel,
  movetype,
  copyFlags,
  dupecount,
  onSubmit,
  onBack,
  onClose,
}) => {
  const [rows, setRows] = useState<Row[]>(() => makeRows(dupecount, sourceValues));
  // Columns the user pulled out of the copy: their field lands BLANK on every
  // new pro (removal blanks the cells; the submit still sends all 18 keys).
  // Deliberately NOT persisted anywhere — a remembered removal would silently
  // blank fields on a future dupe.
  const [removed, setRemoved] = useState<Set<DupeField>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Fresh grid on every open — a previous attempt's typing must never survive
  // (same rule CreateOrderModal applies to its fields). Deliberately NOT keyed
  // on sourceValues: its identity can change mid-open (an orderDetails refresh)
  // and re-prefilling then would clobber the user's typing.
  useEffect(() => {
    if (!open) return;
    setRows(makeRows(dupecount, sourceValues));
    setRemoved(new Set());
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dupecount]);

  // Enter-moves-down needs a live handle per cell; keyed `${rowIndex}:${field}`.
  const cellRefs = useRef(new Map<string, HTMLElement>());

  const columns = useMemo(
    () => COLUMNS.filter((c) => !removed.has(c.key))
      .map((c) => (c.key === 'bol' ? { ...c, label: bolLabel(movetype) } : c)),
    [movetype, removed],
  );

  const setCell = useCallback((index: number, key: DupeField, value: string) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [key]: value } : r)));
  }, []);

  /** V1 `upperit` + `kgconvert`/`mtconvert`, applied as one atomic row update. */
  const handleBlur = useCallback((index: number, key: DupeField) => {
    setRows((prev) => prev.map((r, i) => {
      if (i !== index) return r;
      const raw = r[key] ?? '';
      const normalized = VERBATIM_FIELDS.has(key) ? raw.trim() : raw.trim().toUpperCase();
      const next: Row = normalized === raw ? r : { ...r, [key]: normalized };
      if (!WEIGHT_FIELDS.has(key)) return next;
      const patch = convertWeights(next, key);
      return Object.keys(patch).length === 0 ? next : { ...next, ...patch };
    }));
  }, []);

  /**
   * Move focus to one cell. A picker cell registers its wrapper (DateTimePicker
   * owns the real input), so resolve through it. Returns false when there is no
   * such cell (past the first/last row or column), which is the caller's signal
   * to leave the key alone rather than swallow it.
   *
   * `caret` places the insertion point for the direction of travel: arriving by
   * row selects the text so typing replaces it (and the picker's `0`/`n` hotkeys
   * are a single keystroke), while arriving sideways puts the caret on the edge
   * you came from so continued arrowing reads as one motion.
   */
  const focusCell = useCallback((
    rowIndex: number,
    key: DupeField,
    caret: 'select' | 'start' | 'end',
  ): boolean => {
    const cell = cellRefs.current.get(`${rowIndex}:${key}`);
    const target = cell instanceof HTMLDivElement ? cell.querySelector('input') : cell;
    if (!target) return false;
    target.focus();
    if (target instanceof HTMLInputElement) {
      if (caret === 'select') target.select();
      else if (caret === 'start') target.setSelectionRange(0, 0);
      else target.setSelectionRange(target.value.length, target.value.length);
    }
    return true;
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, index: number, key: DupeField) => {
    if (e.key === 'Enter') {
      // V1's nav_Init/nav_Build grid navigation, reduced to its one useful move:
      // Enter drops to the same column on the next row instead of submitting. A
      // picker cell's own Enter handler has already committed the typed value by
      // the time this bubbles up — so the value sticks AND the focus still moves.
      e.preventDefault();
      focusCell(index + 1, key, 'select');
      return;
    }

    const isSelect = e.target instanceof HTMLSelectElement;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      // Up/Down is how you change a closed <select>'s value, so ON/BY keeps that
      // and travels vertically by Enter. A single-line input has nothing vertical
      // to preserve, so everywhere else these always move a row.
      if (isSelect) return;
      if (focusCell(index + (e.key === 'ArrowDown' ? 1 : -1), key, 'select')) e.preventDefault();
      return;
    }

    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      const back = e.key === 'ArrowLeft';
      // A <select> has no caret to walk, so left/right always leave it — that is
      // what keeps ON/BY from being a cell you can arrow into but not out of, and
      // Up/Down above still sets its value.
      if (!isSelect) {
        // Everywhere else, only leave FROM the caret's edge with nothing selected
        // — otherwise left/right still walk through the text you just typed. A
        // blank cell sits at both edges at once, so it hands off immediately.
        const input = e.target instanceof HTMLInputElement ? e.target : null;
        if (!input) return;
        const { selectionStart, selectionEnd, value } = input;
        if (selectionStart === null || selectionStart !== selectionEnd) return;
        if (back ? selectionStart !== 0 : selectionStart !== value.length) return;
      }
      const nextCol = columns[columns.findIndex((c) => c.key === key) + (back ? -1 : 1)];
      if (nextCol && focusCell(index, nextCol.key, back ? 'end' : 'start')) e.preventDefault();
    }
  }, [columns, focusCell]);

  const removeRow = (index: number) => {
    setError(null);
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const addRow = () => {
    setError(null);
    setRows((prev) => (prev.length >= MAX_DUPE_ROWS ? prev : [...prev, prefillRow(sourceValues)]));
  };

  /** "Don't copy this field": hide the column and blank its cells, so the
   * explicit payload carries '' for it on every row. */
  const removeColumn = (key: DupeField) => {
    if (columns.length <= 1) return;
    setError(null);
    setRemoved((prev) => new Set(prev).add(key));
    setRows((prev) => prev.map((r) => ({ ...r, [key]: '' })));
  };

  /** Bring a column back, RE-PREFILLED from the source — removal already threw
   * the old cells away, so this is a fresh "copy it again", not an undo. */
  const addColumn = (key: DupeField) => {
    if (!key) return;
    setError(null);
    setRemoved((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    const fill = prefillRow(sourceValues)[key];
    setRows((prev) => prev.map((r) => ({ ...r, [key]: fill })));
  };

  const handleSubmit = () => {
    if (busy) return;
    const trimmed: Row[] = rows.map((r) => {
      const out = blankRow();
      for (const c of COLUMNS) out[c.key] = (r[c.key] ?? '').trim();
      return out;
    });
    const dupe = findDuplicateContainer(trimmed);
    if (dupe) {
      setError(`Duplicate container entered on lines ${dupe.first} and ${dupe.second}`);
      return;
    }
    setError(null);
    onSubmit(trimmed);
  };

  // Hooks must run on every render — above the early return (React #310).
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  const inputCls = 'w-full border border-slate-300 dark:border-slate-600 rounded px-1.5 py-1 text-[0.75rem] bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 disabled:opacity-60';
  const cellCls = 'px-1 py-1 align-middle border-b border-slate-100 dark:border-slate-800';
  const headCls = 'px-1 py-1.5 text-left text-[0.6875rem] font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap border-b border-slate-300 dark:border-slate-600';

  const title = `Enter equipment and reference numbers (Master Pro: ${masterProLabel} ${movetype})`.replace(/\s+\)$/, ')');
  const proCount = rows.length;

  return createPortal(
    <div
      data-testid="dupe-grid-backdrop"
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/45 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (!busy && e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        role="dialog"
        aria-label="Enter equipment and reference numbers"
        className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-[min(1200px,96vw)] max-h-[90vh] flex flex-col border border-slate-200 dark:border-slate-700"
      >
        <ModalHeader
          title={title}
          onClose={busy ? undefined : onClose}
          dragHandleProps={dragHandleProps}
          className="shrink-0"
        />

        <div className="px-4 py-3 flex flex-col gap-2 min-h-0">
          <div className="text-[0.75rem] text-slate-700 dark:text-slate-300">
            Copy MRs/BLs/Whse Instructions: <b>{copyFlags.copymrbl}</b>
            {' · '}Notes: <b>{copyFlags.copynotes}</b>
            {' · '}Accessorials: <b>{copyFlags.copyaccessorials}</b>
            {' · '}Documents: <b>{copyFlags.copydocuments}</b>
          </div>
          <p className="text-[0.6875rem] text-slate-500 dark:text-slate-400">
            Cells show what will copy to each new pro — edit or clear them. Removed columns
            don&apos;t copy. Equipment#, LFD and Appt#s never copy automatically.
          </p>

          {/* Only this box scrolls sideways — the page never does. */}
          <div className="overflow-auto max-h-[58vh] border border-slate-200 dark:border-slate-700 rounded">
            <table className="min-w-max text-[0.75rem] border-separate border-spacing-0">
              <thead className="sticky top-0 z-10 bg-slate-100 dark:bg-slate-800">
                <tr>
                  <th scope="col" className={`${headCls} w-10`}><span className="sr-only">Row</span></th>
                  {columns.map((c) => (
                    /* aria-label keeps the header's accessible name at just the
                       label — otherwise the × button's name would ride along. */
                    <th scope="col" key={c.key} aria-label={c.label} className={`${headCls} ${c.width}`}>
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        <button
                          type="button"
                          aria-label={`Remove column ${c.label}`}
                          title="Don't copy this field — it will be blank on all new pros"
                          onClick={() => removeColumn(c.key)}
                          disabled={busy || columns.length <= 1}
                          className="w-4 h-4 leading-none rounded border border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40 disabled:opacity-30"
                        >
                          ×
                        </button>
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr data-testid="dupe-source-row" className="bg-slate-50 dark:bg-slate-800/50">
                  <th scope="row" className={`${cellCls} text-[0.6875rem] font-semibold text-slate-500 dark:text-slate-400 whitespace-nowrap`}>
                    Source
                  </th>
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      data-testid={`dupe-source-${c.key}`}
                      className={`${cellCls} text-slate-600 dark:text-slate-300 whitespace-nowrap`}
                    >
                      {sourceValues[c.key] ?? ''}
                    </td>
                  ))}
                </tr>

                {rows.map((row, i) => (
                  <tr key={i} className="odd:bg-white even:bg-slate-50/60 dark:odd:bg-slate-900 dark:even:bg-slate-800/40">
                    <td className={`${cellCls} whitespace-nowrap`}>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          aria-label={`Remove row ${i + 1}`}
                          title="Remove this row"
                          onClick={() => removeRow(i)}
                          disabled={busy || rows.length <= 1}
                          className="w-5 h-5 leading-none rounded border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40 disabled:opacity-30"
                        >
                          ×
                        </button>
                        <span className="text-[0.6875rem] text-slate-500 dark:text-slate-400">{i + 1}</span>
                      </div>
                    </td>
                    {columns.map((c) => {
                      const label = `Row ${i + 1} ${c.label}`;
                      const refKey = `${i}:${c.key}`;
                      const common = {
                        'aria-label': label,
                        disabled: busy,
                        className: inputCls,
                        onKeyDown: (e: React.KeyboardEvent) => handleKeyDown(e, i, c.key),
                      };
                      return (
                        <td key={c.key} className={cellCls}>
                          {c.kind === 'select' ? (
                            <select
                              {...common}
                              ref={(el) => { if (el) cellRefs.current.set(refKey, el); else cellRefs.current.delete(refKey); }}
                              value={row[c.key]}
                              onChange={(e) => setCell(i, c.key, e.target.value)}
                            >
                              <option value=""></option>
                              <option value="ON">ON</option>
                              <option value="BY">BY</option>
                            </select>
                          ) : c.kind === 'date' || c.kind === 'datetime' ? (
                            /* The app's DateTimePicker instead of a native
                               date/datetime-local input, so these cells get the
                               numeric entry shortcuts (`0` today, `5` +5 days,
                               `-1` yesterday, `n` now) and a real calendar.
                               Portaled to the body — the picker's dropdown is
                               z-[9999] fixed, so it clears both this box's
                               `overflow-auto` and the modal's z-[120]. */
                            <div
                              ref={(el) => { if (el) cellRefs.current.set(refKey, el); else cellRefs.current.delete(refKey); }}
                              onKeyDown={(e) => handleKeyDown(e, i, c.key)}
                            >
                              <DateTimePicker
                                mode={c.kind === 'date' ? 'date' : 'datetime'}
                                selectionType="single"
                                inputVariant="compact"
                                labelType="none"
                                ariaLabel={label}
                                autoApply={c.kind === 'date'}
                                use24Hour
                                usePortal
                                openDirection="down"
                                autoFlip
                                placeholder={c.kind === 'date' ? 'MM/DD/YYYY' : 'MM/DD/YYYY HH:MM'}
                                disabled={busy}
                                className="w-full"
                                value={c.kind === 'date' ? parseDate(row[c.key]) : parseDateTimeLocal(row[c.key])}
                                onChange={(val) => {
                                  const d = val instanceof Date ? val : null;
                                  setCell(i, c.key, c.kind === 'date' ? formatDateStr(d) : formatDateTimeLocal(d));
                                }}
                              />
                            </div>
                          ) : (
                            <input
                              {...common}
                              ref={(el) => { if (el) cellRefs.current.set(refKey, el); else cellRefs.current.delete(refKey); }}
                              type="text"
                              value={row[c.key]}
                              onChange={(e) => setCell(i, c.key, e.target.value)}
                              onBlur={() => handleBlur(i, c.key)}
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={addRow}
              disabled={busy || rows.length >= MAX_DUPE_ROWS}
              className="px-2 py-1 text-[0.75rem] rounded border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40"
            >
              + Add row
            </button>
            <span className="text-[0.6875rem] text-slate-500 dark:text-slate-400">
              {proCount} of {MAX_DUPE_ROWS} rows
            </span>
            {removed.size > 0 && (
              /* Removed columns come back through here, re-prefilled. Rendered
                 only while something is removed — value stays '' so every pick
                 fires onChange, even the same column twice in a session. */
              <select
                aria-label="Add column"
                value=""
                disabled={busy}
                onChange={(e) => addColumn(e.target.value as DupeField)}
                className="px-2 py-1 text-[0.75rem] rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 disabled:opacity-40"
              >
                <option value="" disabled>Add column…</option>
                {COLUMNS.filter((c) => removed.has(c.key)).map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.key === 'bol' ? bolLabel(movetype) : c.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          {error && (
            <div role="alert" className="text-[0.75rem] text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 rounded px-2 py-1.5">
              {error}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-between gap-2 shrink-0">
          <button
            type="button"
            onClick={onBack}
            disabled={busy}
            className="px-3 py-1.5 text-[0.78125rem] font-medium text-slate-700 bg-white border border-slate-300 rounded dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 disabled:opacity-40"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy}
            className="px-4 py-1.5 text-[0.78125rem] font-semibold text-white bg-blue-600 border border-blue-700 rounded hover:bg-blue-700 disabled:opacity-40"
          >
            {busy ? 'Creating…' : `Create ${proCount} Pro${proCount === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DupeOrderGrid;
